import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { PageHeader } from '@/components/ui/page-header'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { useSendSms } from '@/hooks/useSms'
import { useTemplates } from '@/hooks/useTemplates'
import { useSignatures } from '@/hooks/useSignatures'
import { useToast } from '@/hooks/use-toast'
import { Send, Clock, FileText, Users, Calculator, AlertCircle, CheckCircle, Info } from 'lucide-react'
import { format } from 'date-fns'

// 模板短信发送表单验证
const templateSmsSchema = z.object({
  phones: z.string().min(1, '请输入手机号'),
  templateId: z.string().min(1, '请选择短信模板'),
  signatureId: z.string().min(1, '请选择短信签名'),
  variables: z.record(z.string()).optional(), // 模板变量
  scheduledAt: z.string().optional(),
})

type TemplateSmsForm = z.infer<typeof templateSmsSchema>

export default function SmsSend() {
  const [activeTab, setActiveTab] = useState('single')
  const [selectedTemplate, setSelectedTemplate] = useState<any>(null)
  const [selectedSignature, setSelectedSignature] = useState<any>(null)
  const [previewContent, setPreviewContent] = useState('')
  const [phoneCount, setPhoneCount] = useState(0)
  const [templateVariables, setTemplateVariables] = useState<Record<string, string>>({})

  const { toast } = useToast()
  const sendSmsMutation = useSendSms()
  const { data: templates } = useTemplates({ page: 0, size: 100, status: 'APPROVED' })
  const { data: signatures } = useSignatures({ page: 0, size: 100, status: 'APPROVED' })



  // 模板发送表单
  const templateForm = useForm<TemplateSmsForm>({
    resolver: zodResolver(templateSmsSchema),
  })



  // 处理手机号输入
  const handlePhonesChange = (value: string) => {
    const phones = value.split(/[,，\n\s]+/).filter(phone => phone.trim())
    const validPhones = phones.filter(phone => /^1[3-9]\d{9}$/.test(phone.trim()))
    setPhoneCount(validPhones.length)
  }

  // 处理签名选择
  const handleSignatureChange = (signatureId: string) => {
    const signature = signatures?.content?.find(s => s.id.toString() === signatureId)
    if (signature) {
      setSelectedSignature(signature)
      templateForm.setValue('signatureId', signatureId)
      
      // 清空模板选择，因为需要重新选择该签名下的模板
      templateForm.setValue('templateId', '')
      setSelectedTemplate(null)
      setTemplateVariables({})
      setPreviewContent('')
      
      // 临时调试信息
      console.log('Selected signature:', signature)
      console.log('Available templates:', templates?.records)
    }
  }

  // 处理模板选择
  const handleTemplateChange = (templateId: string) => {
    const template = templates?.records?.find(t => t.id.toString() === templateId)
    if (template) {
      setSelectedTemplate(template)
      templateForm.setValue('templateId', templateId)
      
      // 解析模板变量
      const variables = extractTemplateVariables(template.templateContent)
      setTemplateVariables(variables)
      
      // 更新预览内容
      updatePreviewContent(template.templateContent, templateVariables)
      
      // 临时调试信息
      console.log('Selected template:', template)
    }
  }

  // 处理模板变量变化
  const handleVariableChange = (variable: string, value: string) => {
    const newVariables = { ...templateVariables, [variable]: value }
    setTemplateVariables(newVariables)
    updatePreviewContent(selectedTemplate?.templateContent, newVariables)
  }

  // 提取模板变量
  const extractTemplateVariables = (content: string): Record<string, string> => {
    const variables: Record<string, string> = {}
    // 支持两种格式：${variable} 和 {variable}
    const regex = /\$\{(\w+)\}|\{(\w+)\}/g
    let match
    while ((match = regex.exec(content)) !== null) {
      const variableName = match[1] || match[2]
      variables[variableName] = ''
    }
    return variables
  }

  // 更新预览内容
  const updatePreviewContent = (templateContent: string, variables: Record<string, string>) => {
    let content = templateContent || ''
    
    // 替换变量 - 支持两种格式：${variable} 和 {variable}
    Object.entries(variables).forEach(([key, value]) => {
      // 替换 ${variable} 格式
      content = content.replace(new RegExp(`\\$\\{${key}\\}`, 'g'), value || `\${${key}}`)
      // 替换 {variable} 格式
      content = content.replace(new RegExp(`\\{${key}\\}`, 'g'), value || `{${key}}`)
    })
    
    // 添加签名
    if (selectedSignature) {
      content = `【${selectedSignature.name}】${content}`
    }
    
    setPreviewContent(content)
  }

  // 获取当前签名下的模板
  const getTemplatesBySignature = (signatureId: string) => {
    if (!templates?.records) {
      console.log('No templates data available')
      return []
    }
    
    // 过滤出属于当前签名的模板
    const filteredTemplates = templates.records.filter(template => 
      template.signatureId?.toString() === signatureId
    )
    
    // 临时调试信息
    console.log('=== Template Filtering Debug ===')
    console.log('Signature ID:', signatureId, 'Type:', typeof signatureId)
    console.log('All templates count:', templates.records.length)
    console.log('All templates:', templates.records.map(t => ({ id: t.id, name: t.templateName, signatureId: t.signatureId, signatureIdType: typeof t.signatureId })))
    console.log('Filtered templates count:', filteredTemplates.length)
    console.log('Filtered templates:', filteredTemplates.map(t => ({ id: t.id, name: t.templateName, signatureId: t.signatureId })))
    console.log('=== End Debug ===')
    
    return filteredTemplates
  }

  // 模板发送
  const handleTemplateSend = async (data: TemplateSmsForm) => {
    try {
      const phones = data.phones.split(/[,，\n\s]+/).filter(phone => phone.trim())
      const validPhones = phones.filter(phone => /^1[3-9]\d{9}$/.test(phone.trim()))
      
      if (validPhones.length === 0) {
        toast({
          title: '发送失败',
          description: '请输入有效的手机号',
          variant: 'destructive',
        })
        return
      }

      if (!selectedTemplate) {
        toast({
          title: '发送失败',
          description: '请选择短信模板',
          variant: 'destructive',
        })
        return
      }

      // 生成最终的短信内容
      let finalContent = selectedTemplate.templateContent
      Object.entries(templateVariables).forEach(([key, value]) => {
        // 替换 ${variable} 格式
        finalContent = finalContent.replace(new RegExp(`\\$\\{${key}\\}`, 'g'), value || `\${${key}}`)
        // 替换 {variable} 格式
        finalContent = finalContent.replace(new RegExp(`\\{${key}\\}`, 'g'), value || `{${key}}`)
      })

      // 添加签名
      if (selectedSignature) {
        finalContent = `【${selectedSignature.name}】${finalContent}`
      }

      await sendSmsMutation.mutateAsync({
        phones: validPhones,
        content: finalContent,
        templateId: data.templateId,
        signatureId: data.signatureId,
        variables: templateVariables,
        scheduledAt: data.scheduledAt,
      })

      toast({
        title: '发送成功',
        description: `已成功发送给 ${validPhones.length} 个手机号`,
      })

      templateForm.reset()
      setPhoneCount(0)
      setTemplateVariables({})
      setPreviewContent('')
      setSelectedTemplate(null)
      setSelectedSignature(null)
    } catch (error) {
      toast({
        title: '发送失败',
        description: '请稍后重试',
        variant: 'destructive',
      })
    }
  }



  return (
    <div className="flex-1 space-y-4 p-8 pt-6">
      <PageHeader 
        title="短信发送"
        description="使用已审核的模板发送短信，支持单条和批量发送"
      />

      {/* 功能说明 */}
      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          此功能用于测试验证签名、模板以及发送链路。请使用已审核通过的模板和签名进行发送。
        </AlertDescription>
      </Alert>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-1">
          <TabsTrigger value="single">单条发送</TabsTrigger>
        </TabsList>

        {/* 单条发送 */}
        <TabsContent value="single" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-3">
            <div className="md:col-span-2">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Send className="mr-2 h-5 w-5" />
                    模板短信发送
                  </CardTitle>
                  <CardDescription>
                    使用已审核的模板发送短信给一个或多个手机号
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={templateForm.handleSubmit(handleTemplateSend)} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="phones">手机号</Label>
                      <Textarea
                        id="phones"
                        placeholder="请输入手机号，多个号码用逗号、换行或空格分隔"
                        className="min-h-[100px]"
                        {...templateForm.register('phones')}
                        onChange={(e) => {
                          templateForm.setValue('phones', e.target.value)
                          handlePhonesChange(e.target.value)
                        }}
                      />
                      {templateForm.formState.errors.phones && (
                        <p className="text-sm text-red-600">
                          {templateForm.formState.errors.phones.message}
                        </p>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>短信签名 *</Label>
                        <Select 
                          value={templateForm.watch('signatureId')} 
                          onValueChange={handleSignatureChange}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="请选择短信签名" />
                          </SelectTrigger>
                          <SelectContent>
                            {(signatures?.content ?? []).map((signature) => (
                              <SelectItem key={signature.id} value={signature.id.toString()}>
                                {`【${signature.name}】`}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {templateForm.formState.errors.signatureId && (
                          <p className="text-sm text-red-600">
                            {templateForm.formState.errors.signatureId.message}
                          </p>
                        )}
                      </div>

                      <div className="space-y-2">
                        <Label>短信模板 *</Label>
                        <Select 
                          value={templateForm.watch('templateId')}
                          onValueChange={handleTemplateChange}
                          disabled={!selectedSignature}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder={selectedSignature ? "请选择短信模板" : "请先选择签名"} />
                          </SelectTrigger>
                          <SelectContent>
                            {selectedSignature ? 
                              getTemplatesBySignature(selectedSignature.id.toString()).map((template) => (
                                <SelectItem key={template.id} value={template.id.toString()}>
                                  <div className="flex flex-col">
                                    <span>{template.templateName}</span>
                                    <span className="text-xs text-muted-foreground">
                                      {template.templateType}
                                    </span>
                                  </div>
                                </SelectItem>
                              )) : []
                            }
                          </SelectContent>
                        </Select>
                        {templateForm.formState.errors.templateId && (
                          <p className="text-sm text-red-600">
                            {templateForm.formState.errors.templateId.message}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* 模板变量 */}
                    {Object.keys(templateVariables).length > 0 && (
                      <div className="space-y-2">
                        <Label>模板变量</Label>
                        <div className="grid grid-cols-2 gap-4">
                          {Object.keys(templateVariables).map((variable) => (
                            <div key={variable} className="space-y-1">
                              <Label className="text-sm">{variable}</Label>
                              <Input
                                placeholder={`请输入${variable}`}
                                value={templateVariables[variable]}
                                onChange={(e) => handleVariableChange(variable, e.target.value)}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="space-y-2">
                      <Label htmlFor="scheduledAt">定时发送（可选）</Label>
                      <Input
                        id="scheduledAt"
                        type="datetime-local"
                        {...templateForm.register('scheduledAt')}
                        min={format(new Date(), "yyyy-MM-dd'T'HH:mm")}
                      />
                    </div>

                    <Button 
                      type="submit" 
                      className="w-full"
                      disabled={sendSmsMutation.isPending}
                    >
                      {sendSmsMutation.isPending ? '发送中...' : '立即发送'}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* 发送预览 */}
            <div className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <FileText className="mr-2 h-4 w-4" />
                    发送预览
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="p-3 bg-gray-50 rounded-lg border">
                    <div className="text-sm text-gray-600 mb-2">短信预览:</div>
                    <div className="text-sm whitespace-pre-wrap">
                      {previewContent || '请选择签名和模板...'}
                    </div>
                  </div>

                  <Separator />

                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">手机号数量:</span>
                      <Badge variant="outline">
                        <Users className="mr-1 h-3 w-3" />
                        {phoneCount}
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">字数统计:</span>
                      <Badge variant="outline">
                        {previewContent.length}/500
                      </Badge>
                    </div>
                  </div>

                  {/* 发送状态提示 */}
                  {selectedTemplate && selectedSignature && (
                    <Alert>
                      <CheckCircle className="h-4 w-4" />
                      <AlertDescription>
                        模板和签名已选择，可以开始发送测试短信
                      </AlertDescription>
                    </Alert>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>


      </Tabs>
    </div>
  )
}